import {
  GlobalOrderTypeCode,
  OrderSource,
  OrderType,
  ResponseLine,
  ResponseOrderWithCartLines,
  ResponsePromotion,
} from "~/entities/orderEntities";
import { Site } from "~/entities/siteEntities";
import { DiscountType } from "~/entities/promotionEntities";
import { sum } from "~/helpers/arrayHelpers";

export const getBestMatchingSlot = (
  slots: (string | null)[],
  target?: string | null
): string | null => {
  // If no slots return null
  if (!slots.length) {
    return null;
  }

  // If target is asap or not specified, return first slot
  if (target == null) {
    return slots[0];
  }

  // Return latest time that's <= requested
  // Relies on slots being sorted
  const targetTime = new Date(target).getTime();
  for (let i = slots.length - 1; i >= 0; i--) {
    const slot = slots[i];
    if (slot != null && new Date(slot).getTime() <= targetTime) {
      return slot;
    }
  }

  // If one couldn't be found return first slot
  return slots[0];
};

export const paymentMethodIdValid = (
  site: Site,
  paymentMethodId: string | null
) =>
  paymentMethodId == null
    ? site.tableOrderingAllowPayAtTable
    : site.paymentMethods.some((x) => x.id === paymentMethodId);

export const getPaymentMethodId = (
  site: Site
): { paymentMethodId: string | null; valid: boolean } => {
  if (site.paymentMethods.length) {
    return { paymentMethodId: site.paymentMethods[0].id, valid: true };
  }

  return { paymentMethodId: null, valid: site.tableOrderingAllowPayAtTable };
};

export const getOrderTypeCode = (
  orderSource: OrderSource,
  orderTypes: OrderType[]
): string => {
  const match = orderTypes
    .sort((a, b) => a.fulfillmentType.localeCompare(b.fulfillmentType))
    .find((x) => x.source === orderSource);

  if (!match?.code) {
    // For table ordering, always use TABLE_ORDERING_IN_STORE
    if (orderSource === OrderSource.TABLE_ORDERING) {
      return GlobalOrderTypeCode.TABLE_ORDERING_IN_STORE;
    }

    // For online ordering, always use ONLINE_COLLECTION
    else {
      return GlobalOrderTypeCode.ONLINE_COLLECTION;
    }
  }

  return match.code;
};

export const getOrderType = (
  orderTypeCode: string,
  orderTypes: OrderType[]
): OrderType | undefined => orderTypes.find((x) => x.code === orderTypeCode);

export const orderTypeCodeValid = (
  site: Site,
  orderSource: OrderSource,
  orderTypeCode: string
) =>
  site.orderTypes.some(
    (x) => x.code === orderTypeCode && x.source === orderSource
  );

export interface PromotionRequiringSelection {
  promotion: ResponsePromotion;
  requiredSelections: RequiredSelections;
  applied: boolean;
  allowModal: boolean;
}

export const getPromotionsRequiringSelection = (
  orderSummary: ResponseOrderWithCartLines
): PromotionRequiringSelection[] => {
  return orderSummary.unlockedPromotions
    .map((promotion) => {
      const requiredSelections = getPromoSelectionsRequired(
        orderSummary.lines,
        promotion
      );
      return {
        promotion,
        requiredSelections,
        // Separately calculate whether the promotion actually ended up being applied or not
        applied:
          promotion.discountType === DiscountType.ORDER_DISCOUNT
            ? orderSummary.discounts.some(
                (x) => x.promotionCode === promotion.code
              )
            : promotion.discountType === DiscountType.ROOT_ITEM_DISCOUNT
            ? orderSummary.lines.some((x) =>
                x.item.discounts.some((d) => d.promotionCode === promotion.code)
              )
            : orderSummary.lines.some((x) =>
                x.discounts.some((d) => d.promotionCode === promotion.code)
              ),
        // Show the modal for selecting requirements / rewards only if the list of products for what needs selecting is restricted
        allowModal: requiredSelections.requiredQtyRemaining
          ? !!promotion.requiredProductCodes.length
          : requiredSelections.rewardQtyRemaining
          ? !!promotion.productCodes.length
          : false,
      };
    })
    .filter(
      (x) =>
        x.requiredSelections.requiredQtyRemaining ||
        x.requiredSelections.rewardQtyRemaining ||
        !x.applied
    );
};

export interface RequiredSelections {
  requiredQtyRemaining: number;
  rewardQtyRemaining: number;
}

export const getPromoSelectionsRequired = (
  lines: ResponseLine[],
  promotion: ResponsePromotion
): RequiredSelections => {
  const requiredProductCodes = new Set(promotion.requiredProductCodes);
  const requirementLines = promotion.requiredProductCodes?.length
    ? lines.filter((x) => requiredProductCodes.has(x.item.productCode))
    : lines;
  const requirementCount = sum(requirementLines.map((x) => x.item.qty));

  const rewardProductCodes = new Set(promotion.productCodes);
  const rewardLines = promotion.productCodes?.length
    ? lines.filter((x) => rewardProductCodes.has(x.item.productCode))
    : lines;
  const rewardCount = sum(rewardLines.map((x) => x.item.qty));

  // todo Support more complex requirement conditions
  let nRewards = promotion.maxUsesPerRequirements;
  if (
    promotion.maxUsesPerOrder != null &&
    (nRewards == null || promotion.maxUsesPerOrder < nRewards)
  ) {
    nRewards = promotion.maxUsesPerOrder;
  }

  return {
    requiredQtyRemaining: Math.max(
      0,
      (promotion.requiredQty || 1) - requirementCount
    ),
    rewardQtyRemaining: Math.max(0, (nRewards || 0) - rewardCount),
  };
};
